import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
  ExclamationCircleFilled,
  CreditCardOutlined,
  WalletOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Popover } from 'antd';

import generatePath from '~settings/routing';
import { bookingJourneyType } from 'common/constants/booking';

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      ...theme.flex.crossCenter,
      backgroundColor: theme.colors.orange1,
      borderRadius: 2,
      padding: '6px 12px',
      ...theme.typography.body4,
      color: theme.colors.orange6,
    },

    innerWrapper: {
      ...theme.flex.crossCenter,
      display: 'flex',
    },

    popContent: {
      '& > .ant-popover-content': {
        borderRadius: 2,
        '& > .ant-popover-arrow': {
          right: 22,
          borderRightColor: 'rgba(0, 0, 0, 0.75)',
          borderBottomColor: 'rgba(0, 0, 0, 0.75)',
        },
        '& > .ant-popover-inner': {
          padding: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',

          '& .ant-popover-inner-content': {
            padding: 0,
            color: theme.colors.gray1,
          },
        },
      },
    },

    tipIcon: {
      marginRight: 8,
      color: theme.colors.orange11,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    tipButton: {
      ...theme.typography.body6,
      ...theme.flex.crossCenter,
      width: 'fit-content',
      border: `1px solid ${theme.colors.orange2}`,
      borderRadius: 2,
      padding: '0 8px',
      marginLeft: 6,
      cursor: 'pointer',

      '&:hover': {
        borderColor: theme.colors.gray5,
        backgroundColor: theme.colors.gray1,
        color: theme.colors.bodyColor,

        '& > span:first-child': {
          color: theme.colors.disableColor,
        },
      },
    },

    tipButtonIcon: {
      display: 'flex',
      alignItems: 'center',
      marginRight: 6,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    tipButtonRightIcon: {
      marginLeft: 1,

      '& > svg': {
        width: 8,
        height: 8,
      },
    },

    tipsLeft: {
      marginLeft: 6,
    },
  }),
  { name: 'pendingAlertTips' },
);

const PendingAlertTips = ({ property, history, setCurrentSection, from }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const {
    publishCondition: { commissionTier, contractStage, paymentPlan, payout, offlinePaymentMethod },
  } = property;
  const paymentOnline = !property?.paymentPlan || property?.paymentPlan?.paymentOnline;
  const paymentUrl = generatePath('paymentList');
  const payoutUrl = generatePath('withdrawal.list');
  const paymentMethodUrl = generatePath(
    'property.edit',
    { propertySlug: property?.slug },
    { section: 'PAYMENT_METHOD' },
  );
  const isManual = [bookingJourneyType.MANUAL, bookingJourneyType.FULL_AUTOMATIC].includes(
    property?.bookingJourney,
  );

  const handleGotoSet = key => {
    const targetUrl = paymentOnline ? payoutUrl : paymentMethodUrl;
    if (setCurrentSection && !paymentOnline && key !== 'payment') {
      setCurrentSection('PAYMENT_METHOD');
    }
    history.push(key === 'payment' ? paymentUrl : targetUrl);
  };

  const labelContent = key => (
    <Popover
      overlayClassName={styles.popContent}
      content={t(`common.pending_alert_tips.content.${key}`)}
    >
      <span className={styles.tipButton} onClick={() => handleGotoSet(key)}>
        {key === 'payment' ? (
          <WalletOutlined className={styles.tipButtonIcon} />
        ) : (
          <CreditCardOutlined className={styles.tipButtonIcon} />
        )}
        <div>{t(`pending_alert_tips.${key}`)}</div>
        <RightOutlined className={styles.tipButtonRightIcon} />
      </span>
    </Popover>
  );

  const showPaymentMethod = useMemo(() => {
    if (paymentPlan && !paymentOnline) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPlan, property]);

  const isSet = useMemo(
    () => (showPaymentMethod ? offlinePaymentMethod : payout),
    [payout, offlinePaymentMethod, showPaymentMethod],
  );

  if (commissionTier && contractStage && paymentPlan && isSet) {
    return null;
  }

  const tipsConfig = [
    {
      keys: ['payment', 'payout'],
      condition: !isManual && !paymentPlan && paymentOnline && !payout,
    },
    {
      keys: ['payment', 'payment_method'],
      condition: !isManual && !paymentPlan && !paymentOnline && !offlinePaymentMethod,
    },
    {
      keys: ['payment'],
      condition: !isManual && !paymentPlan,
    },
    {
      keys: ['payout'],
      condition: !isManual && paymentOnline && !payout,
    },
    {
      keys: ['payment_method'],
      condition: !isManual && !paymentOnline && !offlinePaymentMethod,
    },
  ];
  const firstTips = tipsConfig.find(item => item.condition);

  const renderTips = keys => (
    <div className={styles.innerWrapper}>
      <span>{t('pending_alert_tips.pre_tips')}</span>
      {keys.map((key, idx) => (
        <React.Fragment key={key}>
          {labelContent(key)}
          {idx < keys.length - 1 ? <span>&nbsp;and</span> : <span>&nbsp;.</span>}
        </React.Fragment>
      ))}
    </div>
  );

  const renderCommissionTips = () => {
    if (!commissionTier && !contractStage) {
      return (
        <span className={styles.tipsLeft}>{t('pending_alert_tips.commission_contract_tips')}</span>
      );
    }
    if (!commissionTier) {
      return <span className={styles.tipsLeft}>{t('pending_alert_tips.commission_tips')}</span>;
    }
    if (!contractStage) {
      return <span className={styles.tipsLeft}>{t('pending_alert_tips.contract_tips')}</span>;
    }
    return null;
  };

  return (
    <div className={styles.wrapper} style={{ marginBottom: from === 'propertyHomepage' && 24 }}>
      <ExclamationCircleFilled className={styles.tipIcon} />
      {firstTips && renderTips(firstTips.keys)}
      {renderCommissionTips()}
    </div>
  );
};

PendingAlertTips.propTypes = {
  property: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default PendingAlertTips;
